import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import {
  StyledBrandKitColumn,
  StyledBrandKitPaletteContainer,
  StyledBrandkitSectionLabels,
} from "../../_components/styled/settings/stylesSettings";
import EditableLabel from "../../_components/common/EditableLabel";
import { TldrConfirmationModal } from "../../_components/common/statelessView";
import BrandkitPalettes from "./BrandkitPalettes";
import {
  addColorPalette,
  deleteBrandKit,
  fetchBrandkitById,
  fetchBrandKits,
  switchBrandkit,
  updateBrandKit,
  updateColorPalette,
} from "../../_actions/brandKitActions";
import BrandKitLogosSection from "./BrandKitLogosSection";
import BrandKitFontsSection from "./BrandKitFontsSection";
import { StyledColorPickerPopover } from "../../_components/styled/details/stylesTextPanel";
import { SketchPicker } from "react-color";
import { DEFAULT_COLOR_PRESETS } from "../../_components/details/constants";
import {
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const addColor = {
  variant: "outline",
  borderStyle: "dotted",
  height: "3.7em",
  width: "3.7em",
  borderWidth: "2px",
  colorScheme: "gray",
  borderColor: "gray.300",
  bg: "none",
  _hover: {
    bg: "none",
    borderColor: "primary.400",
    color: "primary.400",
  },
  _active: { bg: "none" },
};

const BrandkitSection = (props) => {
  const signal = axios.CancelToken.source();

  const { brandKitInfo, fetchBrandkits, assetsDropzoneClassName, fontsDropzoneClassName } = props;
  const { palette } = brandKitInfo;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [inProgress, setInPregress] = useState(false);
  const [newColor, setNewColor] = useState({ r: 0, g: 0, b: 0, a: 1 });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [colors, setColors] = useState([]);

  const dispatch = useDispatch();
  const { t } = useTranslation(["planner"]);

  const brandKit = useSelector(state => state.brandKit);
  const { loaded } = brandKit;

  const workspacePermissionChecks = useSelector(state => state.permissions.workspacePermissionChecks);
  const {
    canEditBrandKit,
    canDeleteBrandKit,
    canViewBrandKitLogo,
    canCreateBrandKitColorPalette,
    canViewBrandKitColorPalette,
    canEditBrandKitColorPalette,
    canViewBrandKitFont,
  } = workspacePermissionChecks;

  function handleFocus(text) { };

  function handleFocusOut(text) {
    if (brandKitInfo.title === text) {
      return
    };

    updateBrandKitInfo(text);
  }

  function updateBrandKitInfo(newTitle) {
    dispatch(
      updateBrandKit(
        brandKitInfo.id,
        newTitle,
        signal
      )
    )
      .then(() => {
        if (fetchBrandkits) {
          dispatch(fetchBrandKits());
        }
      });
  };

  function toggleDeletePopup() {
    setShowDeleteModal(true);
  };

  function deleteSelectedBrandkit() {
    setInPregress(true);

    dispatch(deleteBrandKit(
      brandKitInfo.id,
      signal
    ))
      .then((res) => {
        setInPregress(false);
        setShowDeleteModal(false);
      })
      .catch((err) => {
        setInPregress(false);
        setShowDeleteModal(false);
      });
  };

  function addColorPaletteInSelectedBrandkit() {
    dispatch(
      addColorPalette(
        brandKitInfo.id,
        signal
      )
    )
      .then(() => {
        if (fetchBrandkits) {
          dispatch(fetchBrandKits());
        }
        dispatch(fetchBrandkitById(brandKitInfo.id))
          .then((responseData) => {
            dispatch(switchBrandkit(responseData));
          })
      });
  }

  function addMoreColor() {
    dispatch(
      updateColorPalette(
        brandKitInfo.id,
        palette[0].id,
        { colors: colors },
        signal
      )
    )
      .then(() => {
        if (fetchBrandkits) {
          dispatch(fetchBrandKits());
        }
        dispatch(fetchBrandkitById(brandKitInfo.id))
          .then((responseData) => {
            dispatch(switchBrandkit(responseData));
          })
      });
  };

  return (
    <>
      <VStack>
        {canEditBrandKit && (
          <Flex
            direction="row"
            align="center"
            justify="space-between"
            h="36px"
            mx="0"
            my="4"
            w="100%"
          >
            <EditableLabel
              text={brandKitInfo.title}
              labelClassName="brandkitname"
              inputClassName="brandkitinput"
              labelPlaceHolder={brandKitInfo.title}
              inputMaxLength={50}
              onFocus={handleFocus}
              onFocusOut={handleFocusOut}
            />

            {canDeleteBrandKit && (
              <Tooltip hasArrow label={t("brandkit.delete", { bk_name: brandKitInfo.title })}>
                <IconButton
                  icon={<FaTrash />}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDeletePopup();
                  }}
                  variant="ghost"
                />
              </Tooltip>
            )}
          </Flex>
        )}

        {canViewBrandKitLogo && (
          <BrandKitLogosSection brandKitInfo={brandKitInfo} assetsDropzoneClassName={assetsDropzoneClassName} />
        )}

        {canViewBrandKitFont && (
          <BrandKitFontsSection brandKitInfo={brandKitInfo} fontsDropzoneClassName={fontsDropzoneClassName} />
        )}

        {canViewBrandKitColorPalette && (
          <StyledBrandKitColumn>
            <StyledBrandkitSectionLabels>
              <label>{t("brandkit.color")}</label>
            </StyledBrandkitSectionLabels>
            <StyledBrandKitPaletteContainer>
              {palette && palette.length > 0 ? (
                <>
                  {renderBrandkitPaletteElement()}
                  {canEditBrandKitColorPalette && (
                    <Menu
                      isLazy
                      isOpen={isMenuOpen}
                      onClose={() => {
                        addMoreColor();
                        setIsMenuOpen(false);
                      }}
                    >
                      <MenuButton
                        as={IconButton}
                        icon={<FaPlus />}
                        sx={addColor}
                        position="relative"
                        onClick={() => {
                          var colorsArray = palette[0].colors;
                          colorsArray.push({ rgb: `rgba(217,217,217,1)` });
                          setColors([...colorsArray]);

                          setIsMenuOpen(true);
                        }}
                        disabled={isMenuOpen}
                      />

                      <MenuList>
                        <StyledColorPickerPopover
                          showBrandkitPaletteColors={false}
                        >
                          <SketchPicker
                            disableAlpha={true}
                            presetColors={DEFAULT_COLOR_PRESETS}
                            color={newColor}
                            onChange={({ rgb, hex }) => {
                              var colorsArray = palette[0].colors;
                              colorsArray[colorsArray.length - 1] = { rgb: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})` };
                              setColors([...colorsArray]);

                              setNewColor(rgb);
                            }}
                          />
                        </StyledColorPickerPopover>
                      </MenuList>
                    </Menu>
                  )}
                </>
              ) : (
                <>
                  {canCreateBrandKitColorPalette && (
                    <Tooltip
                      placement="top"
                      hasArrow
                      label={t("brandkit.add_palette")}
                    >
                      <IconButton
                        icon={<FaPlus />}
                        sx={addColor}
                        onClick={addColorPaletteInSelectedBrandkit}
                      />
                    </Tooltip>
                  )}
                </>
              )}
            </StyledBrandKitPaletteContainer>
          </StyledBrandKitColumn>
        )}
      </VStack>

      {showDeleteModal ? (
        <div onClick={e => e.stopPropagation()}>
          <TldrConfirmationModal
            modalFor="Brandkit"
            inProgress={inProgress}
            show={showDeleteModal}
            onHide={(e) => {
              setShowDeleteModal(false);
            }}
            onYes={deleteSelectedBrandkit}
          />
        </div>
      ) : null}
    </>
  );

  function renderBrandkitPaletteElement() {
    if (loaded) {
      return (
        <>
          {palette && palette.map((colorPalette, idx) => (
            <React.Fragment key={idx}>
              {renderColorsElementFromPalette(colorPalette)}
            </React.Fragment>
          ))}
        </>
      );
    } else {
      return <></>;
    }
  };

  function renderColorsElementFromPalette(colorPalette) {
    return (
      <>
        {colorPalette?.colors.map((color, index) => (
          <BrandkitPalettes
            key={index}
            color={color}
            brandKit={brandKitInfo}
            palette={colorPalette}
          />
        ))}
      </>
    );
  };
};

BrandkitSection.propTypes = {};

BrandkitSection.defaultProps = {
  assetsDropzoneClassName: "",
  fontsDropzoneClassName: "",
};

export default BrandkitSection;